import { Box, Chip, Divider, Stack, Typography } from "@mui/material";
import type {
	SoilInterpretationDTO,
	SoilPhysicsResultDTO,
	SoilTestDTO,
} from "@flaha/shared-types";

import { GlassSurface } from "../../../layouts/components/GlassSurface";
import { OVERALL_RATING_HELP } from "../utils/agronomicCopy";
import { ratingToStatus } from "../utils/interpretationStatus";
import { PROVENANCE_LABELS } from "../utils/provenanceLabels";
import { SpecialNote } from "./SpecialNote";

interface SummaryCardProps {
	soilTest: SoilTestDTO;
	physics: SoilPhysicsResultDTO | null | undefined;
	interpretation: SoilInterpretationDTO | null | undefined;
}

/** Presents persisted result metadata only; it derives no scientific values. */
export function SummaryCard({ soilTest, physics, interpretation }: SummaryCardProps) {
	const overall = ratingToStatus(interpretation?.overallSoilRating);
	const ratingHelp = interpretation
		? OVERALL_RATING_HELP[interpretation.overallSoilRating]
		: null;

	return (
		<GlassSurface
			component="section"
			aria-labelledby="result-summary-title"
			intensity="strong"
			depth="floating"
			sx={{
				p: { xs: 2.25, md: 3 },
				mb: 3,
				backgroundImage:
					"linear-gradient(120deg, rgba(119,169,87,0.1), transparent 48%), linear-gradient(300deg, rgba(74,156,197,0.08), transparent 50%)",
			}}
		>
			<Stack
				direction={{ xs: "column", md: "row" }}
				spacing={{ xs: 2.5, md: 4 }}
				divider={<Divider orientation="vertical" flexItem sx={{ borderColor: "workspace.border" }} />}
			>
				<Box sx={{ minWidth: { md: 260 } }}>
					<Typography
						variant="overline"
						sx={{ color: "workspace.green", letterSpacing: "0.12em", fontWeight: 700 }}
					>
						Soil intelligence summary
					</Typography>
					<Typography id="result-summary-title" component="h1" variant="h4" sx={{ mt: 0.5 }}>
						What this sample says
					</Typography>
					<Typography variant="caption" sx={{ color: "workspace.textMuted", display: "block", mt: 0.5 }}>
						{PROVENANCE_LABELS.interpreted}
					</Typography>
					{interpretation ? (
						<Stack spacing={1.25} sx={{ mt: 1.5 }}>
							<Chip
								label={`Overall rating: ${interpretation.overallSoilRating}`}
								color={overall.color}
								sx={{ alignSelf: "flex-start", fontWeight: 700 }}
							/>
							{ratingHelp ? (
								<SpecialNote kind="guidance" title="What this rating means" dense>
									{ratingHelp}
								</SpecialNote>
							) : null}
						</Stack>
					) : (
						<Typography variant="body2" sx={{ color: "workspace.textSecondary", mt: 1.5 }}>
							Interpretation is unavailable for this test.
						</Typography>
					)}
				</Box>

				<Box sx={{ flex: 1, minWidth: 0 }}>
					<Typography component="h2" variant="subtitle1" fontWeight={700}>
						Test evidence
					</Typography>
					<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
						<Chip
							label={`Reported scope: ${soilTest.testLevel}`}
							variant="outlined"
							size="small"
						/>
						{physics?.textureClass ? (
							<Chip
								label={`Classified texture: ${physics.textureClass}`}
								variant="outlined"
								size="small"
							/>
						) : null}
						{soilTest.labName ? (
							<Chip
								label={`Reported lab: ${soilTest.labName}`}
								variant="outlined"
								size="small"
							/>
						) : null}
						{soilTest.labReference ? (
							<Chip
								label={`Lab reference: ${soilTest.labReference}`}
								variant="outlined"
								size="small"
							/>
						) : null}
					</Stack>
					<Typography
						variant="body2"
						sx={{ color: "workspace.textSecondary", mt: 2, maxWidth: 520 }}
					>
						Values below stay separated by type: calculated physics, calculated
						chemistry when available, and agronomic interpretation. Model
						estimates are labeled where they replace lab measurements.
					</Typography>
				</Box>
			</Stack>
		</GlassSurface>
	);
}
